import { useMemo, useState, useCallback } from 'react'
import {
  Table,
  Tooltip,
  Typography,
  Popover,
  Space,
  Pagination,
} from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import {
  RESEARCH_DEPTH_OPTIONS,
} from '../../utils/constants'

const DEPTH_STYLES = {
  1: { label: 'Shallow', style: { color: '#4338ca', backgroundColor: '#eef2ff', borderColor: '#c7d2fe' } },
  2: { label: 'Medium', style: { color: '#7c3aed', backgroundColor: '#f3e8ff', borderColor: '#e9d5ff' } },
  3: { label: 'Deep', style: { color: '#0369a1', backgroundColor: '#e0f2fe', borderColor: '#bae6fd' } },
  4: { label: 'Very Deep', style: { color: '#0f766e', backgroundColor: '#d1fae5', borderColor: '#a7f3d0' } },
  5: { label: 'Maximum', style: { color: '#92400e', backgroundColor: '#fef3c7', borderColor: '#fde68a' } },
  default: { label: 'Depth', style: { color: '#1f2937', backgroundColor: '#f8fafc', borderColor: '#e2e8f0' } },
}

const ANALYST_STYLE = { color: '#0f172a', backgroundColor: '#f1f5f9', borderColor: '#e2e8f0' }
const TRANSACTION_TYPE_STYLE = { color: '#0f172a', backgroundColor: '#ecfeff', borderColor: '#bae6fd' }

const TRANSACTION_TYPE_LABELS = {
  SINGLE_TASK: 'One-Time',
  SUBSCRIPTION: 'Subscription',
  SUBSCRIPTION_PAYMENT: 'Subscription Payment',
  SUBSCRIPTION_REFUND: 'Subscription Refund',
}

const { Text } = Typography

const PurchaseList = ({
  data = [],
  loading = false,
  onChange,
  pagination,
  isMobile = false,
}) => {
  const navigate = useNavigate()
  const [activeBreakdownId, setActiveBreakdownId] = useState(null)

  const handleViewTask = useCallback((record) => {
    if (!record?.taskId) {
      return
    }
    navigate(`/tasks/${record.taskId}`)
  }, [navigate])

  const isRefundTransaction = useCallback((record) => (
    (record?.transactionType || '').toUpperCase().endsWith('_REFUND')
  ), [])

  const formatAmount = useCallback((record) => {
    if (record?.isFree) {
      return <Text type="success">Free</Text>
    }
    const formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: record?.currency || 'USD',
      minimumFractionDigits: 2,
    }).format(record?.amount || 0)

    if (isRefundTransaction(record)) {
      return <Text type="danger">{`-${formattedAmount}`}</Text>
    }
    return formattedAmount
  }, [isRefundTransaction])

  const formatDate = useCallback((value, format = 'YYYY-MM-DD HH:mm:ss') => {
    if (!value) return '-'
    return dayjs(value).format(format)
  }, [])

  const formatCurrencyValue = useCallback((amount, currency) => (
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
    }).format(amount || 0)
  ), [])

  const renderSoftBadge = useCallback((label, palette) => (
    <span className="purchase-history-soft-tag" style={palette}>
      {label}
    </span>
  ), [])

  const isSubscriptionTransaction = useCallback((record) => (
    (record?.transactionType || '').toUpperCase().startsWith('SUBSCRIPTION')
  ), [])

  const normalizeDepthInfo = useCallback((value) => {
    if (value == null) {
      return DEPTH_STYLES.default
    }

    const numericValue = Number(value)
    if (Number.isFinite(numericValue) && DEPTH_STYLES[numericValue]) {
      return DEPTH_STYLES[numericValue]
    }

    const valueString = String(value).toLowerCase()
    const matchedOption = RESEARCH_DEPTH_OPTIONS.find((option) => (
      String(option.label).toLowerCase() === valueString
    ))

    if (matchedOption && DEPTH_STYLES[matchedOption.value]) {
      return {
        label: matchedOption.label,
        style: DEPTH_STYLES[matchedOption.value].style,
      }
    }

    return {
      label: typeof value === 'string' ? value : DEPTH_STYLES.default.label,
      style: DEPTH_STYLES.default.style,
    }
  }, [])

  const renderTransactionTypeTag = useCallback((record) => {
    const typeKey = (record.transactionType || '').toUpperCase()
    const label = TRANSACTION_TYPE_LABELS[typeKey] || record.transactionType || '-'
    return renderSoftBadge(label, TRANSACTION_TYPE_STYLE)
  }, [renderSoftBadge])

  const buildPricingDetails = useCallback((record) => {
    if (!record?.pricingSnapshot) return null

    try {
      const snapshot = JSON.parse(record.pricingSnapshot)

      const toNumber = (value, fallback = null) => {
        const num = Number(value)
        return Number.isFinite(num) ? num : fallback
      }

      const currency = snapshot.currency || record.currency || 'USD'
      const basePrice = toNumber(snapshot.basePrice, 0)
      const totalAmount = toNumber(snapshot.totalAmount, record.amount)
      const researchDepthFactor = toNumber(snapshot.researchDepthFactor, 1)
      const analystFactor = toNumber(snapshot.analystFactor, 1)

      const formatNumeric = (value, fraction = 2) => (
        Number.isFinite(value) ? value.toFixed(fraction) : '0.00'
      )

      const calculationFormula = `${currency} ${formatNumeric(basePrice)} × ${formatNumeric(researchDepthFactor)} (depth) × ${formatNumeric(analystFactor)} (analysts) = ${currency} ${formatNumeric(totalAmount)}`

      return {
        pricing: {
          basePrice,
          researchDepthFactor,
          analystFactor,
          totalAmount,
          currency,
          calculationFormula,
        },
        researchDepth: snapshot.researchDepth,
        analystCount: snapshot.analystCount,
        selectedAnalysts: Array.isArray(snapshot.selectedAnalysts)
          ? snapshot.selectedAnalysts
          : [],
      }
    } catch (error) {
      console.warn('Failed to parse pricing snapshot:', error)
      return null
    }
  }, [])

  const handlePopoverOpen = useCallback((recordKey, open) => {
    setActiveBreakdownId(open ? recordKey : null)
  }, [])

  const renderPricingPopoverContent = useCallback((record) => {
    if (isSubscriptionTransaction(record)) {
      const totalDisplay = formatCurrencyValue(record?.amount, record?.currency)
      const isRefund = isRefundTransaction(record)

      return (
        <div style={{
          maxWidth: 280,
          padding: '16px',
          background: 'rgba(17, 24, 39, 0.95)',
          backdropFilter: 'blur(12px)',
          borderRadius: 12,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        }}>
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ fontSize: 14, color: '#f9fafb', letterSpacing: '-0.01em' }}>
              {isRefund ? 'Subscription Refund' : 'Monthly Subscription Payment'}
            </Text>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <Text style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.6)' }}>
              {isRefund ? 'Amount Refunded' : 'Amount Paid'}
            </Text>
            <Text strong style={{ fontSize: 16, color: isRefund ? '#f87171' : '#fbbf24', letterSpacing: '-0.02em' }}>
              {isRefund ? `-${totalDisplay}` : totalDisplay}
            </Text>
          </div>
        </div>
      )
    }

    const details = buildPricingDetails(record)
    if (!details) return null

    const depthOption = RESEARCH_DEPTH_OPTIONS.find(
      (option) => option.value === details.researchDepth,
    )
    const depthLabel = depthOption?.label
      || (typeof details.researchDepth === 'string' ? details.researchDepth : 'N/A')
    const analystCount = Number.isFinite(details.analystCount)
      ? details.analystCount
      : (Array.isArray(details.selectedAnalysts) ? details.selectedAnalysts.length : 0)
    const totalDisplay = record.isFree
      ? 'Free'
      : formatCurrencyValue(details.pricing.totalAmount, details.pricing.currency)

    return (
      <div style={{
        maxWidth: 280,
        padding: '16px',
        background: 'rgba(17, 24, 39, 0.95)',
        backdropFilter: 'blur(12px)',
        borderRadius: 12,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
      }}>
        <div style={{ marginBottom: 14 }}>
          <Text strong style={{ fontSize: 14, color: '#f9fafb', letterSpacing: '-0.01em' }}>
            💰 Pricing Breakdown
          </Text>
        </div>
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.6)' }}>Research Depth</Text>
            <Text strong style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.9)' }}>
              {depthLabel}
            </Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.6)' }}>Analysts Selected</Text>
            <Text strong style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.9)' }}>
              {analystCount}
            </Text>
          </div>
          {details.pricing.calculationFormula && (
            <>
              <div style={{
                height: 1,
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
                margin: '4px 0',
              }} />
              <div>
                <Text style={{
                  fontSize: 11,
                  color: 'rgba(255, 255, 255, 0.5)',
                  display: 'block',
                  marginBottom: 6,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  Calculation
                </Text>
                <div style={{
                  fontSize: 11,
                  color: '#fbbf24',
                  fontFamily: 'SF Mono, Monaco, Courier New, monospace',
                  background: 'rgba(251, 191, 36, 0.15)',
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid rgba(251, 191, 36, 0.2)',
                  lineHeight: 1.5,
                }}>
                  {details.pricing.calculationFormula}
                </div>
              </div>
            </>
          )}
          <div style={{
            height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
            margin: '4px 0',
          }} />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0 4px',
          }}>
            <Text strong style={{ fontSize: 13, color: '#f9fafb' }}>Total Amount</Text>
            <Text strong style={{
              fontSize: 16,
              color: '#fbbf24',
              letterSpacing: '-0.02em',
              textShadow: '0 0 20px rgba(251, 191, 36, 0.3)',
            }}>
              {totalDisplay}
            </Text>
          </div>
        </Space>
      </div>
    )
  }, [buildPricingDetails, formatCurrencyValue, isRefundTransaction, isSubscriptionTransaction])

  const handleInteractiveClick = useCallback((event) => {
    event.stopPropagation()
  }, [])

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Stock',
      key: 'stock',
      align: 'left',
      render: (_, record) => (
        <Space size={2} direction="vertical" className="purchase-list-stock">
          <Text
            className="purchase-list-stock__ticker"
            style={{ fontSize: 16, whiteSpace: 'nowrap' }}
          >
            {record.ticker}
          </Text>
          {!isSubscriptionTransaction(record) && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.analysisDate ? dayjs(record.analysisDate).format('YYYY-MM-DD') : '-'}
            </Text>
          )}
        </Space>
      ),
      width: 160,
    },
    {
      title: 'Type',
      key: 'transactionType',
      align: 'center',
      render: (_, record) => renderTransactionTypeTag(record),
      width: 140,
    },
    {
      title: 'Depth',
      key: 'depth',
      align: 'center',
      render: (_, record) => {
        const pricingDetails = buildPricingDetails(record)
        const depthValue = pricingDetails?.researchDepth ?? record.researchDepth ?? record.research_depth
        const depthConfig = normalizeDepthInfo(depthValue)
        return renderSoftBadge(depthConfig.label, depthConfig.style)
      },
      width: 140,
    },
    {
      title: 'Analysts',
      key: 'analysts',
      align: 'center',
      render: (_, record) => {
        const pricingDetails = buildPricingDetails(record)
        const selected = pricingDetails?.selectedAnalysts
          ?? record.selectedAnalysts
          ?? record.selected_analysts
        const analystNames = Array.isArray(selected) ? selected.filter(Boolean) : []
        let analystCount = pricingDetails?.analystCount
          ?? record.analystCount
          ?? record.analyst_count

        if (!analystCount && analystNames.length > 0) {
          analystCount = analystNames.length
        }

        if (!analystCount) {
          return <Text type="secondary">-</Text>
        }

        const label = `${analystCount} ${analystCount === 1 ? 'Analyst' : 'Analysts'}`
        const badge = renderSoftBadge(label, ANALYST_STYLE)

        return analystNames.length > 0 ? (
          <Tooltip title={analystNames.join(', ')}>
            {badge}
          </Tooltip>
        ) : badge
      },
      width: 150,
    },
    {
      title: 'Amount',
      key: 'amount',
      align: 'right',
      render: (_, record) => {
        const recordKey = record.paymentId ?? record.taskId
        const amountNode = (
          <Text strong style={{ fontSize: 14 }}>
            {formatAmount(record)}
          </Text>
        )

        const popoverContent = renderPricingPopoverContent(record)
        if (!popoverContent) {
          return amountNode
        }

        return (
          <Space size={6}>
            {amountNode}
            <Popover
              content={popoverContent}
              title={null}
              trigger="click"
              open={activeBreakdownId === recordKey}
              onOpenChange={(open) => handlePopoverOpen(recordKey, open)}
              placement="bottomRight"
              overlayInnerStyle={{
                padding: 0,
                background: 'transparent',
                boxShadow: 'none',
              }}
            >
              <span onClick={handleInteractiveClick}>
                <QuestionCircleOutlined
                  style={{ color: '#6366f1', cursor: 'pointer' }}
                />
              </span>
            </Popover>
          </Space>
        )
      },
      width: 150,
    },
    {
      title: 'Date',
      key: 'paidAt',
      align: 'right',
      render: (_, record) => (
        record.paidAt ? (
          <Tooltip title={formatDate(record.paidAt)}>
            <Text type="secondary">
              {formatDate(record.paidAt, 'YYYY-MM-DD HH:mm:ss')}
            </Text>
          </Tooltip>
        ) : <Text type="secondary">-</Text>
      ),
      width: 140,
    },
    ]

    const getHeaderClass = (alignValue) => {
      if (alignValue === 'right') return 'purchase-history-header-right'
      if (alignValue === 'center') return 'purchase-history-header-center'
      return 'purchase-history-header-left'
    }

    return baseColumns.map((column) => {
      const alignValue = column.align || 'left'
      return {
        ...column,
        onHeaderCell: () => ({ className: getHeaderClass(alignValue) }),
      }
    })
  }, [
    activeBreakdownId,
    buildPricingDetails,
    formatAmount,
    formatDate,
    handleInteractiveClick,
    handlePopoverOpen,
    renderPricingPopoverContent,
    renderSoftBadge,
    renderTransactionTypeTag,
    normalizeDepthInfo,
    isRefundTransaction,
    isSubscriptionTransaction,
  ])

  const mergedPagination = {
    current: pagination?.current ?? 1,
    pageSize: pagination?.pageSize ?? 10,
    total: pagination?.total ?? (Array.isArray(data) ? data.length : 0),
  }

  const handleMobilePageChange = useCallback((page, pageSize) => {
    if (typeof onChange === 'function') {
      onChange({
        current: page,
        pageSize,
      })
    }
  }, [onChange])

  const handleMobilePageSizeChange = useCallback((currentPage, newSize) => {
    if (typeof onChange === 'function') {
      onChange({
        current: currentPage,
        pageSize: newSize,
      })
    }
  }, [onChange])

  const locale = {
    emptyText: (
      <div style={{
        padding: '60px 0',
        textAlign: 'center',
        color: '#9ca3af',
      }}>
        <div style={{ fontSize: '16px', marginBottom: '8px' }}>
          No transaction records found
        </div>
        <div style={{ fontSize: '12px', color: '#6b7280' }}>
          Your transaction history will appear here once payments or refunds are recorded
        </div>
      </div>
    ),
  }

  if (isMobile) {
    const hasData = Array.isArray(data) && data.length > 0

    return (
      <div className="purchase-list-mobile mobile-record-list">
        {hasData ? (
          <div className="purchase-list-mobile__cards mobile-record-cards">
            {data.map((record) => {
              const recordKey = record.paymentId ?? record.taskId ?? record.ticker
              const isSubscription = isSubscriptionTransaction(record)
              const analysisDate = record.analysisDate
                ? dayjs(record.analysisDate).format('YYYY-MM-DD')
                : '-'
              const transactionType = TRANSACTION_TYPE_LABELS[(record.transactionType || '').toUpperCase()]
                || record.transactionType
                || '-'
              const popoverContent = renderPricingPopoverContent(record)
              const paidDisplay = record.paidAt
                ? formatDate(record.paidAt, 'YYYY-MM-DD HH:mm:ss')
                : '-'
              const amountValue = record.isFree ? (
                <Text type="success" strong style={{ fontSize: 18 }}>
                  Free
                </Text>
              ) : isRefundTransaction(record) ? (
                <Text type="danger" strong style={{ fontSize: 18 }}>
                  {`-${formatCurrencyValue(record.amount, record.currency)}`}
                </Text>
              ) : (
                <Text strong style={{ fontSize: 18 }}>
                  {formatCurrencyValue(record.amount, record.currency)}
                </Text>
              )

              return (
                <div
                  key={recordKey}
                  className="purchase-card mobile-record-card"
                  onClick={() => handleViewTask(record)}
                  role={record?.taskId ? 'button' : undefined}
                  tabIndex={record?.taskId ? 0 : -1}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleViewTask(record)
                    }
                  }}
                  style={{ cursor: record?.taskId ? 'pointer' : 'default' }}
                >
                  <div className="purchase-card__header mobile-record-card__header">
                    <Text
                      className="purchase-card__ticker mobile-record-card__title"
                    >
                      {record.ticker}
                    </Text>
                  </div>

                  <div className="purchase-card__amount mobile-record-card__amount">
                    <div className="purchase-card__amount-value mobile-record-card__amount-value">
                      {amountValue}
                    </div>
                    {popoverContent && (
                      <Popover
                        content={popoverContent}
                        title={null}
                        trigger="click"
                        open={activeBreakdownId === recordKey}
                        onOpenChange={(open) => handlePopoverOpen(recordKey, open)}
                        placement="bottomRight"
                        overlayInnerStyle={{
                          padding: 0,
                          background: 'transparent',
                          boxShadow: 'none',
                        }}
                      >
                        <span onClick={handleInteractiveClick}>
                          <QuestionCircleOutlined style={{ color: '#6366f1', cursor: 'pointer' }} />
                        </span>
                      </Popover>
                    )}
                  </div>

                  <div className="purchase-card__details mobile-record-card__details">
                    {!isSubscription && (
                      <div className="purchase-card__detail mobile-record-card__detail">
                        <Text type="secondary" className="purchase-card__detail-label mobile-record-card__detail-label">
                          Analysis Date
                        </Text>
                        <Text className="purchase-card__detail-value mobile-record-card__detail-value">
                          {analysisDate}
                        </Text>
                      </div>
                    )}
                    <div className="purchase-card__detail mobile-record-card__detail">
                      <Text type="secondary" className="purchase-card__detail-label mobile-record-card__detail-label">
                        Type
                      </Text>
                      <Text className="purchase-card__detail-value mobile-record-card__detail-value">
                        {transactionType}
                      </Text>
                    </div>
                    <div className="purchase-card__detail mobile-record-card__detail">
                      <Text type="secondary" className="purchase-card__detail-label mobile-record-card__detail-label">
                        Date
                      </Text>
                      <Text className="purchase-card__detail-value mobile-record-card__detail-value">
                        {paidDisplay}
                      </Text>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          !loading && (
            <div className="purchase-list-mobile__empty mobile-record-empty">
              {locale.emptyText}
            </div>
          )
        )}

        {mergedPagination.total > 0 && (
          <Pagination
            className="purchase-list-mobile__pagination mobile-record-pagination"
            current={mergedPagination.current}
            total={mergedPagination.total}
            pageSize={mergedPagination.pageSize}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) => (
              `${range[0]}-${range[1]} of ${total} records`
            )}
            pageSizeOptions={['10', '20', '50', '100']}
            onChange={handleMobilePageChange}
            onShowSizeChange={handleMobilePageSizeChange}
            size="small"
          />
        )}
      </div>
    )
  }

  return (
    <Table
      columns={columns}
      dataSource={data}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => (
          `${range[0]}-${range[1]} of ${total} records`
        ),
        pageSizeOptions: ['10', '20', '50', '100'],
        size: 'default',
      }}
      onChange={onChange}
      rowKey={(record) => `${record.transactionType || 'UNKNOWN'}-${record.paymentId ?? record.taskId}`}
      onRow={(record) => ({
        onClick: () => handleViewTask(record),
        style: { cursor: record?.taskId ? 'pointer' : 'default' },
      })}
      scroll={{ x: true }}
      locale={locale}
      size="middle"
    />
  )
}

export default PurchaseList
